import { isWithinInterval } from 'date-fns';
import { selectNormalizedCategoriesWithTransactionsCount } from 'reducers/selectors';
// "45" matches amounts that round or truncate to 45
// "45." matches 45.00–45.99 and "45.5" matches 45.50–45.59
// "45.50" matches 45.50 only
export const refineNumberSearch = (n, amount, nativeAmount, decimals = Number.isInteger(n) ? undefined : 2) => {
    const absNumber = Math.abs(n);
    if (decimals === undefined) {
        const absTransactionAmount = Math.abs(Math.trunc(amount));
        const absNativeAmount = Math.abs(Math.trunc(nativeAmount));
        const absTransactionAmountRound = Math.abs(Math.round(amount));
        const absNativeAmountRound = Math.abs(Math.round(nativeAmount));
        return (absNumber === absTransactionAmount ||
            absNumber === absNativeAmount ||
            absNumber === absTransactionAmountRound ||
            absNumber === absNativeAmountRound);
    }
    // compare in pennies so floating point can't push 45.59 below 45.5
    const scale = 10 ** Math.min(decimals, 2);
    const typed = Math.round(absNumber * scale);
    const truncate = (value) => Math.trunc(Math.round(Math.abs(value) * 100) / (100 / scale));
    return typed === truncate(amount) || typed === truncate(nativeAmount);
};
export const extractNumbers = (input) => {
    const words = input.split(' ').filter((word) => !/\p{L}/u.test(word));
    const numbers = [];
    for (const word of words) {
        const sanitizedWord = word.replace(/[^0-9.-]+/g, '');
        const num = parseFloat(sanitizedWord);
        if (!Number.isNaN(num) && Number.isFinite(num)) {
            const point = sanitizedWord.indexOf('.');
            numbers.push({
                value: num,
                decimals: point === -1 ? undefined : sanitizedWord.length - point - 1,
            });
        }
    }
    return numbers;
};
export const getFilteredTransactions = (transactions, settingsFilter, fuse, dataInput, activeStateFilter) => {
    if (settingsFilter === 'OR') {
        return transactions?.filter((transaction) => {
            if (activeStateFilter.CATEGORY) {
                const shouldPassThisCheck = activeStateFilter.CATEGORY.some((element) => 'id' in element && transaction.categoryId === element.id);
                if (shouldPassThisCheck) {
                    return true;
                }
            }
            if (activeStateFilter.MERCHANT) {
                const shouldPassThisCheck = activeStateFilter.MERCHANT.some((element) => 'displayName' in element &&
                    transaction.merchant?.displayName === element.displayName);
                if (shouldPassThisCheck) {
                    return true;
                }
            }
            if (activeStateFilter.TAG) {
                const shouldPassThisCheck = activeStateFilter.TAG.some((element) => 'label' in element &&
                    transaction.labels
                        ?.map((label) => label.toLowerCase())
                        .includes(element.label.toLowerCase()));
                if (shouldPassThisCheck) {
                    return true;
                }
            }
            if (activeStateFilter.ACCOUNT) {
                const shouldPassThisCheck = activeStateFilter.ACCOUNT.some((element) => 'id' in element && element.id === transaction.accountId);
                if (shouldPassThisCheck) {
                    return true;
                }
            }
            if (activeStateFilter.AMOUNT_LOWER &&
                !!activeStateFilter.AMOUNT_LOWER.value &&
                !(Math.abs(transaction.amount) >=
                    Number(activeStateFilter.AMOUNT_LOWER.value.replace(/,/g, '')))) {
                return true;
            }
            if (activeStateFilter.AMOUNT_UPPER &&
                !!activeStateFilter.AMOUNT_UPPER.value &&
                !(Math.abs(transaction.amount) <=
                    Number(activeStateFilter.AMOUNT_UPPER.value.replace(/,/g, '')))) {
                return true;
            }
            if (activeStateFilter.DATES) {
                const date = transaction.customDate || transaction.bookingDate;
                if (date &&
                    isWithinInterval(date, {
                        start: activeStateFilter.DATES.startDate,
                        end: activeStateFilter.DATES.endDate,
                    })) {
                    return true;
                }
            }
            if (activeStateFilter.RECEIPT && transaction.receiptId) {
                return true;
            }
            if (!dataInput.length) {
                return false;
            }
            let shouldBeShown = false;
            fuse.setCollection([transaction]);
            for (const searchTerm of dataInput) {
                const result = fuse.search(searchTerm);
                const numbers = extractNumbers(searchTerm || '');
                // the score: 0 is perfecting match, 1 is no match, can be adjusted to get different search results
                // https://www.fusejs.io/api/options.html#includescore
                if (!(result.length === 0 || (result[0]?.score ?? 1) > 0.2)) {
                    shouldBeShown = true;
                    break;
                }
                for (const number of numbers) {
                    if (refineNumberSearch(number.value, transaction.amount ?? Number.MIN_SAFE_INTEGER, transaction.nativeAmount?.amount ?? Number.MIN_SAFE_INTEGER, number.decimals)) {
                        shouldBeShown = true;
                        break;
                    }
                }
                if (shouldBeShown) {
                    break;
                }
            }
            return shouldBeShown;
        });
    }
    return transactions?.filter((transaction) => {
        if (activeStateFilter.CATEGORY) {
            const shouldPassThisCheck = activeStateFilter.CATEGORY.some((element) => 'id' in element && transaction.categoryId === element.id);
            if (!shouldPassThisCheck) {
                return false;
            }
        }
        if (activeStateFilter.MERCHANT) {
            const shouldPassThisCheck = activeStateFilter.MERCHANT.some((element) => 'displayName' in element &&
                transaction.merchant?.displayName === element.displayName);
            if (!shouldPassThisCheck) {
                return false;
            }
        }
        if (activeStateFilter.TAG) {
            const shouldPassThisCheck = activeStateFilter.TAG.some((element) => 'label' in element &&
                transaction.labels
                    ?.map((label) => label.toLowerCase())
                    .includes(element.label.toLowerCase()));
            if (!shouldPassThisCheck) {
                return false;
            }
        }
        if (activeStateFilter.ACCOUNT) {
            const shouldPassThisCheck = activeStateFilter.ACCOUNT.some((element) => 'id' in element && element.id === transaction.accountId);
            if (!shouldPassThisCheck) {
                return false;
            }
        }
        if (activeStateFilter.AMOUNT_LOWER &&
            !!activeStateFilter.AMOUNT_LOWER.value &&
            Math.abs(transaction.amount) >=
                Number(activeStateFilter.AMOUNT_LOWER.value.replace(/,/g, ''))) {
            return false;
        }
        if (activeStateFilter.AMOUNT_UPPER &&
            !!activeStateFilter.AMOUNT_UPPER.value &&
            Math.abs(transaction.amount) <=
                Number(activeStateFilter.AMOUNT_UPPER.value.replace(/,/g, ''))) {
            return false;
        }
        if (activeStateFilter.DATES) {
            const date = transaction.customDate || transaction.bookingDate;
            if (!(date &&
                isWithinInterval(date, {
                    start: activeStateFilter.DATES.startDate,
                    end: activeStateFilter.DATES.endDate,
                }))) {
                return false;
            }
        }
        if (activeStateFilter.RECEIPT && !transaction.receiptId) {
            return false;
        }
        if (!dataInput.length) {
            return true;
        }
        if (settingsFilter === 'AND') {
            let shouldBeShown = true;
            fuse.setCollection([transaction]);
            for (const searchTerm of dataInput) {
                let shouldBeShownInternal = false;
                const result = fuse.search(searchTerm);
                const numbers = extractNumbers(searchTerm || '');
                // the score: 0 is perfecting match, 1 is no match, can be adjusted to get different search results
                // https://www.fusejs.io/api/options.html#includescore
                if (!(result.length === 0 || (result[0]?.score ?? 1) > 0.2)) {
                    shouldBeShownInternal = true;
                }
                for (const number of numbers) {
                    if (refineNumberSearch(number.value, transaction.amount ?? Number.MIN_SAFE_INTEGER, transaction.nativeAmount?.amount ?? Number.MIN_SAFE_INTEGER, number.decimals)) {
                        shouldBeShownInternal = true;
                        break;
                    }
                }
                if (!shouldBeShownInternal) {
                    shouldBeShown = false;
                    break;
                }
            }
            return shouldBeShown;
        }
        let shouldBeShown = false;
        fuse.setCollection([transaction]);
        for (const searchTerm of dataInput) {
            const result = fuse.search(searchTerm);
            const numbers = extractNumbers(searchTerm || '');
            // the score: 0 is perfecting match, 1 is no match, can be adjusted to get different search results
            // https://www.fusejs.io/api/options.html#includescore
            if (!(result.length === 0 || (result[0]?.score ?? 1) > 0.2)) {
                shouldBeShown = true;
                break;
            }
            for (const number of numbers) {
                if (refineNumberSearch(number.value, transaction.amount ?? Number.MIN_SAFE_INTEGER, transaction.nativeAmount?.amount ?? Number.MIN_SAFE_INTEGER, number.decimals)) {
                    shouldBeShown = true;
                    break;
                }
            }
            if (shouldBeShown) {
                break;
            }
        }
        return shouldBeShown;
    });
};
/**
 * handles only subcategories and parent categories which have subcategories
 * does not handle AND settings filter
 */
export const handleSelectCategoryFilter = (currentFilter, newFilter, store) => {
    const categories = selectNormalizedCategoriesWithTransactionsCount(store);
    const currentFilterMap = currentFilter.reduce((acc, curr) => {
        acc[curr.id] = curr;
        return acc;
    }, {});
    let filteredCategoriesFilter = [...currentFilter];
    const currentFilterCategory = currentFilterMap[newFilter.id] &&
        currentFilterMap[newFilter.id].badgeText === newFilter.badgeText
        ? currentFilterMap[newFilter.id]
        : undefined;
    if (!newFilter.parentCategoryId) {
        const subcategories = newFilter.subCategoryIds
            ?.map((id) => categories[id])
            .filter((subcategory) => !!subcategory);
        const hasSubcategorySelected = currentFilter?.some((curr) => curr.parentCategoryId === newFilter.id);
        if (currentFilterCategory || hasSubcategorySelected) {
            filteredCategoriesFilter = filteredCategoriesFilter.filter((curr) => curr.id !== newFilter.id && curr.parentCategoryId !== newFilter.id);
        }
        else {
            filteredCategoriesFilter.push(newFilter);
            if (subcategories?.length) {
                filteredCategoriesFilter.push(...subcategories.map((subcategory) => ({
                    ...subcategory,
                    badgeText: subcategory.displayName,
                    filterType: 'CATEGORY',
                    isUserSelected: false,
                })));
            }
        }
        return filteredCategoriesFilter;
    }
    const hasSubcategoriesSelected = currentFilter?.some((curr) => curr.parentCategoryId === newFilter.parentCategoryId &&
        curr.isUserSelected);
    if (currentFilterCategory?.isUserSelected) {
        filteredCategoriesFilter = filteredCategoriesFilter.filter((curr) => curr.id !== newFilter.id);
        const isUnselectingLastSubcategory = currentFilter.filter((curr) => curr.parentCategoryId === newFilter.parentCategoryId).length === 1;
        const parentCategory = categories[newFilter.parentCategoryId];
        if (isUnselectingLastSubcategory && parentCategory) {
            const subcategories = parentCategory.subCategoryIds
                ?.map((id) => categories[id])
                .filter((subcategory) => !!subcategory);
            filteredCategoriesFilter.push({
                ...parentCategory,
                badgeText: parentCategory.displayName,
                filterType: 'CATEGORY',
                isUserSelected: false,
            });
            if (subcategories?.length) {
                filteredCategoriesFilter.push(...subcategories.map((subcategory) => ({
                    ...subcategory,
                    badgeText: subcategory.displayName,
                    filterType: 'CATEGORY',
                    isUserSelected: false,
                })));
            }
        }
    }
    else if (hasSubcategoriesSelected) {
        filteredCategoriesFilter = filteredCategoriesFilter.filter((curr) => curr.id !== newFilter.id);
        filteredCategoriesFilter.push(newFilter);
    }
    else {
        const parentCategory = currentFilter.find((curr) => curr.id === newFilter.parentCategoryId);
        if (parentCategory) {
            filteredCategoriesFilter = filteredCategoriesFilter.filter((curr) => curr.id !== newFilter.id &&
                curr.parentCategoryId !== newFilter.parentCategoryId &&
                curr.id !== newFilter.parentCategoryId);
        }
        filteredCategoriesFilter.push(newFilter);
    }
    return filteredCategoriesFilter;
};
